import { Box, Button, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type React from 'react';
import type { Tender } from '../../types/tender';

interface TenderGoNoGoDecisionProps {
  tender: Tender;
  hasAddedContextFiles: boolean;
  forceEnablePursue: boolean;
  onSetForceEnablePursue: (value: boolean) => void;
  onArchive: () => void;
  onPursue: () => void;
}

const TenderGoNoGoDecision: React.FC<TenderGoNoGoDecisionProps> = ({
  tender,
  hasAddedContextFiles,
  forceEnablePursue,
  onSetForceEnablePursue,
  onArchive,
  onPursue,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mt: 4,
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.info.main, 0.05),
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" color="primary" gutterBottom>
        You have not yet made a Go/No-Go decision on this tender
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        To unlock document drafting, proposal structure generation, and review capabilities, you
        must explicitly pursue this tender. Pursuing this tender will count towards your active
        limits.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
        <Button
          variant="outlined"
          color="error"
          size="large"
          sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 4, py: 1.5 }}
          onClick={onArchive}
        >
          No Go (Archive)
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={tender.noFiles && !hasAddedContextFiles && !forceEnablePursue}
          sx={{ fontSize: '1.2rem', fontWeight: 'bold', px: 4, py: 1.5 }}
          onClick={onPursue}
        >
          Go (Pursue)
        </Button>
      </Box>

      {tender.noFiles && !hasAddedContextFiles && !forceEnablePursue && (
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body2" color="warning.main" sx={{ mb: 1, maxWidth: 600 }}>
            The <strong>Go (Pursue)</strong> button is currently disabled because there are no files
            associated with this tender. Please upload context documents above to enable automated
            analysis and drafting.
          </Typography>
          <Button
            variant="text"
            color="warning"
            size="small"
            onClick={() => onSetForceEnablePursue(true)}
            sx={{ fontWeight: 'bold' }}
          >
            Go Anyway (Bypass Document Check)
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TenderGoNoGoDecision;
